//@ts-strict-ignore
/*
 * Component to maintain a list of customer/nominal budgets for 12 periods
 * Value passed is an array of budgets per financial year
 */
import React, { useEffect, useState } from 'react';
import QueryDropdown from '../../../components/connectedForm/QueryDropdown';
import { BlurTextField } from '../../../components/connectedForm/inputComponents';
import {
  ICustomerBudgetInput,
  IFinancialCalendar,
  IFinancialPeriod,
  INominalBudgetInput,
  INominalBudgetProfileInput,
  Maybe,
} from '../../../graphql/schema/p4types';
import { BlurTextFieldType } from '../BlurTextField/BlurTextField';
import { DocumentNode } from 'graphql';

export type IBudget = INominalBudgetInput | ICustomerBudgetInput;

export type IPeriodBudgetsProps = {
  financialYear: Maybe<IFinancialCalendar>;
  profileDropdown: {
    query: DocumentNode;
    valueKey: string;
    labelKey: string;
  };
  value: Maybe<Array<IBudget>>;
  onChange?: (budget: Array<Maybe<IBudget>>) => void;
};

const PeriodBudgets = (props: IPeriodBudgetsProps) => {
  const [financialYear, setFinancialYear] = useState<Maybe<IFinancialCalendar>>(props.financialYear);
  const [value, setValue] = useState<Maybe<Array<IBudget>>>(props.value);
  const [profile, setProfile] = useState<Maybe<INominalBudgetProfileInput>>(null);
  const [totalBudget, setTotalBudget] = useState<Maybe<number>>(null);
  // Tracks whether the last field entry / UI change was to the period
  // values or to the other fields (budget profile or budget total)
  const [isChangeToPeriodValues, setIsChangeToPeriodValues] = useState<Maybe<boolean>>(null);

  useEffect(() => {
    setFinancialYear(props.financialYear);
  }, [props.financialYear]);

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  useEffect(() => {
    if (financialYear && value) {
      const budgetsForYear: Maybe<IBudget> = getBudgetsForYear(financialYear);
      if (budgetsForYear) {
        if (isChangeToPeriodValues === null) {
          // No changes made in the UI yet, set budget profile and total from saved values
          setProfile(budgetsForYear.budgetProfile);
          setTotalBudget(budgetsForYear.totalBudget);
        } else if (isChangeToPeriodValues) {
          // Period values have been edited, so update budget total from new values and clear
          // budget profile (profile is not compatible with manual entry of period values)
          updateBudgetTotal(budgetsForYear);
          setProfile(null);
          budgetsForYear.budgetProfile = null;
        }
      }
    }
  }, [financialYear, value, isChangeToPeriodValues]);

  useEffect(() => {
    // Calculate and update period values based on the budget total and budget profile, if
    // either are changed
    if (totalBudget && isChangeToPeriodValues !== null && !isChangeToPeriodValues) {
      updatePeriodBudgets(totalBudget);
    }
  }, [totalBudget, isChangeToPeriodValues, profile]);

  // find the budgets for the expected financial year
  const getBudgetsForYear = (year: Maybe<IFinancialCalendar>): Maybe<IBudget> => {
    let budgets: Maybe<IBudget> = null;

    if (year && value) {
      budgets = value.find((budget) => budget.financialCalendar?.id === year.id);
    }

    return budgets;
  };

  /*
   * create a text input for each of the 12 periods
   */
  const createPeriodBudgets = (): Array<JSX.Element> => {
    if (financialYear) {
      const periodList: Maybe<Array<Maybe<IFinancialPeriod>>> = financialYear.financialPeriodList;
      let index = 1;
      const budgets: Maybe<IBudget> = getBudgetsForYear(financialYear);
      if (periodList) {
        return periodList.filter((period) => !!period).map((period) => inputFields(period, budgets, index++));
      }
    }
    return [];
  };

  const newBudget = (financialCalendar: Maybe<IFinancialCalendar>): Maybe<IBudget> => {
    return {
      financialCalendar,
      budgetProfile: profile,
      period1: 0,
      period2: 0,
      period3: 0,
      period4: 0,
      period5: 0,
      period6: 0,
      period7: 0,
      period8: 0,
      period9: 0,
      period10: 0,
      period11: 0,
      period12: 0,
    } as IBudget;
  };

  // merge the budgets for the year being maintained with any other existing years and then call form onChange
  const updateBudget = (budget: Maybe<IBudget>) => {
    let budgets: Array<Maybe<IBudget>> = Array.isArray(value) && value.length > 0 ? [...value] : [];
    if (!getBudgetsForYear(financialYear)) {
      budgets.push(budget);
    }
    budgets = budgets.map((existingBudget) =>
      existingBudget?.financialCalendar?.id === budget?.financialCalendar?.id ? budget : existingBudget
    );
    props.onChange?.(budgets);
  };

  // handle budget total input and calculate new period budgets
  const updatePeriodBudgets = (total: number) => {
    if (total) {
      const budgets: Maybe<IBudget> = getBudgetsForYear(financialYear);
      const budget: Maybe<IBudget> = Object.assign({}, budgets ? budgets : newBudget(financialYear));
      const values: Array<string> = calculateBudgets(total);
      let idx: number = 1;
      values.forEach((periodValue) => (budget[`period${idx++}`] = periodValue));

      budget.budgetProfile = profile;
      updateBudget(budget);
    }
  };

  // Update budget total based on addition of all period values
  const updateBudgetTotal = (budgets: IBudget) => {
    setTotalBudget(
      parseFloat(budgets.period1) +
        parseFloat(budgets.period2) +
        parseFloat(budgets.period3) +
        parseFloat(budgets.period4) +
        parseFloat(budgets.period5) +
        parseFloat(budgets.period6) +
        parseFloat(budgets.period7) +
        parseFloat(budgets.period8) +
        parseFloat(budgets.period9) +
        parseFloat(budgets.period10) +
        parseFloat(budgets.period11) +
        parseFloat(budgets.period12)
    );
  };

  // Calculate new period budgets based on either the profile or equal split Period 12 is set to the remaining budget
  const calculateBudgets = (total: number): Array<string> => {
    const values: Array<string> = [];
    if (profile) {
      let amount: number = 0;
      for (let idx = 1; idx < 12; idx++) {
        const periodValue: number = (total * profile[`period${idx}`]) / 100;
        const periodBudget: string = Number.parseFloat(periodValue.toString()).toFixed(2);
        values.push(periodBudget);
        amount += +periodBudget;
      }
      values.push((total - amount).toFixed(2));
    } else {
      const periodValue: number = total / 12;
      const periodBudget: string = Number.parseFloat(periodValue.toString()).toFixed(2);

      let amount = 0;
      for (let idx = 1; idx < 12; idx++) {
        values.push(periodBudget);
        amount += +periodBudget;
      }

      values.push((total - amount).toFixed(2));
    }
    return values;
  };

  // handle the user input and update period budget value
  const onInputFieldChange = (value: number, index: number): void => {
    const budgetsForYear: Maybe<IBudget> = getBudgetsForYear(financialYear);

    const budgets = Object.assign({}, budgetsForYear ? budgetsForYear : newBudget(financialYear));
    budgets[`period${index}`] = value;
    updateBudget(budgets);
  };

  const inputFields = (period: Maybe<IFinancialPeriod>, budget: Maybe<IBudget>, index: number) => {
    const periodValue: string = budget ? budget[`period${index}`] : '';
    return (
      <BlurTextField
        labelCols={1 / 3}
        inputCols={1 / 3}
        value={periodValue}
        reformatLabel={false}
        onChange={(value) => {
          setIsChangeToPeriodValues(true);
          onInputFieldChange(value, index);
        }}
        label={period?.name}
        key={`budget${index}`}
        type={BlurTextFieldType.Currency}
        enableOnChange
      />
    );
  };

  let periodBudgets: Array<JSX.Element> = [];
  if (financialYear && financialYear.financialPeriodList) {
    periodBudgets = createPeriodBudgets();
  }

  return (
    <>
      <QueryDropdown
        {...props.profileDropdown}
        label="Budget Profile"
        onChange={(value) => {
          setIsChangeToPeriodValues(false);
          setProfile(value);
        }}
        value={profile}
        valueKey="id"
        labelKey="description"
        labelCols={1 / 3}
        inputCols={2 / 3}
      />
      <BlurTextField
        labelCols={1 / 3}
        inputCols={1 / 3}
        value={totalBudget}
        onChange={(value) => {
          setIsChangeToPeriodValues(false);
          setTotalBudget(value);
        }}
        label="Total Budget"
        type={BlurTextFieldType.Currency}
        enableOnChange
      />
      {periodBudgets.length ? (
        periodBudgets
      ) : (
        <div>
          <br />
          Financial calendar has not been setup
        </div>
      )}
    </>
  );
};

export default PeriodBudgets;
